import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Badge } from 'react-bootstrap';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { lighten } from 'polished';
import {
  colorAccent,
  colorHover,
  colorText,
  sidebarColor,
  colorRed,
  colorGray,
} from '@/utils/palette';
import {
  left,
  marginLeft,
  marginRight,
} from '@/utils/directions';
import { SideBarRefreshContext } from '../../../../context/SideBarRefreshContext';

// Create a session storage key based on route
const getClickCountKey = route => `sidebar_click_count_${route}`;

const SidebarLink = ({
  title, icon, newLink, route, onClick, color,
}) => {
  const location = useLocation();
  const clickCountKey = getClickCountKey(route);
  const { sideBarRefresh, setSideBarRefresh } = useContext(SideBarRefreshContext);

  
  // Track if this is the first click or not
  const [clickCount, setClickCount] = useState(0);
  
  // Initialize click count from session storage when component mounts
  useEffect(() => {
    const storedCount = sessionStorage.getItem(clickCountKey);
    if (storedCount) {
      setClickCount(parseInt(storedCount, 10));
    }
  }, [clickCountKey]);

  const handleClick = (e) => {
    // Get current count from state
    const currentCount = clickCount + 1;
  
    // Update count in state and session storage
    setClickCount(currentCount);
    sessionStorage.setItem(clickCountKey, currentCount.toString());
  
    // Call the provided onClick handler
    if (onClick) {
      onClick(e);
    }
  
    // First click: normal NavLink behavior
    // Subsequent clicks on same route: just log the route
    if (currentCount > 1 && location.pathname === route) {
      console.log('Navigated again to:', route);
    }
  };
  

  // Effect for click for second time in same Route for should be refresh that page
  useEffect(() => {
    if (clickCount > 1) {
      // For Restaurant Details 
      if (location.pathname === '/agent/Profile') {
        setSideBarRefresh(prevState => ({
          ...prevState,
          agentProfileRefresh: !prevState.agentProfileRefresh,
        }));
      }
      // For Restaurant Meal 
      if (location.pathname === 'agent/meal') {
        setSideBarRefresh(prevState => ({
          ...prevState,
          agentMealRefresh: !prevState.agentMealRefresh,
        }));
      }
      // For Restaurant Meal Request
      if (location.pathname === '/agent/booking') {
        setSideBarRefresh(prevState => ({
          ...prevState,
          agentBookingRefresh: !prevState.agentBookingRefresh,
        }));
      }
      // For Restaurant Meal Rejected Request Refresh
      if (location.pathname === '/agent/trip') {
        setSideBarRefresh(prevState => ({
          ...prevState,
          agentTripRefresh: !prevState.agentTripRefresh,
        }));
      }

      // Booking Refresh
      if (location.pathname === '/agent/account') {
        setSideBarRefresh(prevState => ({
          ...prevState,
          agentAccountRefresh: !prevState.agentAccountRefresh,
        }));
      }

      // Account Refresh
      if (location.pathname === '/agent/payment/approved') {
        setSideBarRefresh(prevState => ({
          ...prevState,
          agentApprovedRefresh: !prevState.agentApprovedRefresh,
        }));
      }
     
      // Account Approved Refresh 
      if (location.pathname === '/agent/payment/pending') {
        setSideBarRefresh(prevState => ({
          ...prevState,
          agentpendingRefresh: !prevState.agentpendingRefresh,
        }));
      }
      // Account Reject Refresh 
      if (location.pathname === '/agent/payment/reject') {
        setSideBarRefresh(prevState => ({
          ...prevState,
          agentrejectRefresh: !prevState.agentrejectRefresh,
        }));
      }
      // Account Pending Refresh 
      if (location.pathname === '/usermanager/users') {
        setSideBarRefresh(prevState => ({
          ...prevState,
          agentUserRefresh: !prevState.agentUserRefresh,
        }));
      }
    }
  }, [clickCount, location.pathname]);
  


  
  return (
    <li>
      <SidebarNavLink
        to={route}
        onClick={handleClick}
        activeClassName="active"
      >
        {icon ? (
          <SidebarLinkIcon
            style={{
              color: 'skyblue',
            }}
            className={`lnr lnr-${icon}`}
          />
        ) : ''}
        <SidebarLinkTitle
          style={{
            color: color || colorText,
          }}
        >
          {title}
          {newLink ? (
            <NewBadge bg="custom">
              <span>New</span>
            </NewBadge>
          ) : ''}
        </SidebarLinkTitle>
      </SidebarNavLink>
    </li>
  );
};

SidebarLink.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  newLink: PropTypes.bool,
  route: PropTypes.string,
  onClick: PropTypes.func,
  color: PropTypes.string,
};

SidebarLink.defaultProps = {
  icon: '',
  newLink: false,
  route: '/',
  onClick: () => {},
  color: '',
};

export default SidebarLink;

// region STYLES

export const SidebarNavLink = styled(NavLink)`
  height: 36px;
  width: 240px;
  transition: all 0.3s;
  position: relative;
  cursor: pointer;
  display: flex;
  padding: 11px 20px;
  overflow: hidden;
  background: transparent;
  border: none;
  color: ${colorText};
  text-align: ${left};
  font-size: 14px;

  &.active {
    background: ${sidebarColor};

    &:before {
      opacity: 1;
    }
  }

  &:before {
    content: "";
    position: absolute;
    top: 0;
    height: 100%;
    width: 2px;
    background: ${colorAccent};
    opacity: 0;
    transition: all 0.3s;
    ${left}: 0;
  }

  &:hover {
    text-decoration: none;
    background-color: ${colorHover};
    color: ${colorText};

    &:before {
      opacity: 1;
    }
  }

  @media screen and (min-width: 576px) {
    width: 100%;

    span {
      position: relative;
      animation: none;
      ${left}: 0;
    }
  }
`;

const NewBadge = styled(Badge)`
  width: 26px;
  height: 14px;
  background-color: ${colorRed};
  font-size: 8px;
  font-weight: 400;
  padding: 2px;
  line-height: 9px;
  position: relative;
  text-transform: uppercase;
  border-radius: 7px;
  ${marginLeft}: 5px;

  span {
    position: absolute;
    top: 3px;
    width: 26px;
    text-align: center;
    ${left}: 0;
  }
`;

export const SidebarLinkTitle = styled.span`
  margin: 0;
  font-size: 14px;
  line-height: 16px;
  position: relative;
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

export const SidebarLinkIcon = styled.span`
  font-size: 13px;
  line-height: 13px;
  color: ${lighten(0.25, colorGray)};
  ${marginRight}: 10px;
`;


// import React from 'react';
// import PropTypes from 'prop-types';
// import { Badge } from 'react-bootstrap';
// import { NavLink } from 'react-router-dom';
// import styled from 'styled-components';
// import { lighten } from 'polished';
// import {
//   colorAccent,
//   colorHover,
//   colorText,
//   sidebarColor,
//   colorRed,
//   colorGray,
// } from '@/utils/palette';
// import { 
//   left,
//   marginLeft,
//   marginRight,
// } from '@/utils/directions';

// const SidebarLink = ({
//   title, icon, newLink, route, onClick, color,
// }) => (
//   <li>
//     <SidebarNavLink
//       to={route}
//       onClick={onClick}
//       activeClassName="active"
//     >
//       {icon ? (
//         <SidebarLinkIcon
//           style={{
//         color: 'skyblue',
//       }}
//           className={`lnr lnr-${icon}`}
//         />
// ) : ''}
//       <SidebarLinkTitle style={{
//         color: color || colorText,
//        }}
//       >
//         {title}
//         {newLink ? (
//           <NewBadge bg="custom">
//             <span>New</span>
//           </NewBadge>
//         ) : ''}
//       </SidebarLinkTitle>
//     </SidebarNavLink>
//   </li>
// );

// SidebarLink.propTypes = {
//   title: PropTypes.string.isRequired,
//   icon: PropTypes.string,
//   newLink: PropTypes.bool,
//   route: PropTypes.string,
//   onClick: PropTypes.func,
//   color: PropTypes.string,
// };

// SidebarLink.defaultProps = {
//   icon: '',
//   newLink: false,
//   route: '/',
//   onClick: () => {},
//   color: '',
// };

// export default SidebarLink;

// // region STYLES

// export const SidebarNavLink = styled(NavLink)`
//   height: 36px;
//   width: 240px;
//   transition: all 0.3s;
//   position: relative;
//   cursor: pointer;
//   display: flex;
//   padding: 11px 20px;
//   overflow: hidden;
//   background: transparent;
//   border: none;
//   color: ${colorText};
//   text-align: ${left};
//   font-size: 14px;

//   &.active {
//     background: ${sidebarColor};

//     &:before {
//       opacity: 1;
//     }
//   }

//   &:before {
//     content: "";
//     position: absolute;
//     top: 0;
//     height: 100%;
//     width: 2px;
//     background: ${colorAccent};
//     opacity: 0;
//     transition: all 0.3s;
//     ${left}: 0;
//   }

//   &:hover {
//     text-decoration: none;
//     background-color: ${colorHover};
//     color: ${colorText};

//     &:before {
//       opacity: 1;
//     }
//   }

//   @media screen and (min-width: 576px) {
//     width: 100%;

//     span {
//       position: relative;
//       animation: none;
//       ${left}: 0;
//     }
//   }
// `;

// const NewBadge = styled(Badge)`
//   width: 26px;
//   height: 14px;
//   background-color: ${colorRed};
//   font-size: 8px;
//   font-weight: 400;
//   padding: 2px;
//   line-height: 9px;
//   position: relative;
//   text-transform: uppercase;
//   border-radius: 7px;
//   ${marginLeft}: 5px;

//   span {
//     position: absolute;
//     top: 3px;
//     width: 26px;
//     text-align: center;
//     ${left}: 0;
//   }
// `;

// export const SidebarLinkTitle = styled.span`
//   margin: 0;
//   font-size: 14px;
//   line-height: 16px;
//   position: relative;
//   display: flex;
//   align-items: center;
//   white-space: nowrap;
// `;

// export const SidebarLinkIcon = styled.span`
//   font-size: 13px;
//   line-height: 13px;
//   color: ${lighten(0.25, colorGray)};
//   ${marginRight}: 10px;
// `;

// // endregion
